import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import {
  useUserMemberships,
  useActiveTeam,
  useSwitchTeam,
  useIsTeamAdmin as useIsTeamAdminHook,
  teamKeys,
} from '@/hooks/useTeams';

interface Team {
  id: string;
  name: string;
  description?: string;
  budget?: number;
  median?: number;
  optimal?: number;
}

interface Membership {
  id: string;
  user_id: string;
  team_id: string;
  is_admin: boolean;
  is_accepted: boolean;
  team: Team;
}

interface TeamContextType {
  activeTeamId: string | null;
  activeTeam: Team | null;
  teams: Team[];
  memberships: Membership[];
  isLoadingTeams: boolean;
  error: string | null;
  switchTeam: (teamId: string) => Promise<void>;
  refreshTeams: () => Promise<void>;
  isTeamAdmin: (teamId?: string) => boolean;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};

export const TeamProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Use TanStack Query hooks
  const { data: membershipsData, isLoading: isLoadingMemberships } = useUserMemberships();
  const { data: activeTeamData, isLoading: isLoadingActiveTeam } = useActiveTeam();
  const switchTeamMutation = useSwitchTeam();

  // Transform memberships data - use the data directly from the service
  const memberships: Membership[] = useMemo(() => {
    if (!membershipsData) return [];
    return membershipsData;
  }, [membershipsData]);

  const teams = useMemo(() => {
    return memberships.map(m => m.team).filter(Boolean);
  }, [memberships]);

  const activeTeam = activeTeamData || null;
  const activeTeamId = activeTeam?.id || null;

  const isLoadingTeams = isLoadingMemberships || isLoadingActiveTeam;

  // Switch to a different team
  const switchTeam = useCallback(async (teamId: string) => {
    if (!user) {
      toast.error('You must be logged in to switch teams');
      return;
    }

    // Verify user is a member of this team
    const membership = memberships.find(m => m.team_id === teamId);
    if (!membership) {
      toast.error('You are not a member of this team');
      return;
    }

    try {
      await switchTeamMutation.mutateAsync({ userId: user.id, teamId });
      toast.success(`Switched to ${membership.team.name}`);
    } catch (err: unknown) {
      console.error('Error switching teams:', err);
      toast.error('Failed to switch teams');
    }
  }, [user, memberships, switchTeamMutation]);

  // Check if user is admin of a team
  const isTeamAdmin = useCallback((teamId?: string) => {
    const targetTeamId = teamId || activeTeamId;
    if (!targetTeamId) return false;

    const membership = memberships.find(m => m.team_id === targetTeamId);
    return membership?.is_admin || false;
  }, [activeTeamId, memberships]);

  // Refresh teams data by invalidating both memberships and active team caches,
  // which forces useUserMemberships and useActiveTeam to re-fetch from Supabase.
  const refreshTeams = useCallback(async () => {
    if (!user?.id) return;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: teamKeys.memberships(user.id) }),
      queryClient.invalidateQueries({ queryKey: teamKeys.activeTeam(user.id) }),
    ]);
  }, [queryClient, user?.id]);

  const value: TeamContextType = {
    activeTeamId,
    activeTeam,
    teams,
    memberships,
    isLoadingTeams,
    error: null, // TanStack Query handles errors differently
    switchTeam,
    refreshTeams,
    isTeamAdmin,
  };

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
};

export { TeamContext };

